import { useEffect, useRef, useState, forwardRef, useContext } from 'react';
import {
  ChevronRight,
  ChevronDown,
  ChevronsDownUp,
  ChevronsUpDown,
  ArrowUpToLine,
  ArrowDownToLine,
  CornerDownRight,
  Link as LinkIcon,
  Pencil,
  Settings,
  Trash2,
  GripVertical,
} from 'lucide-react';
import { displayUri } from './menuTreeApi';
import { MenuTreeCtx } from './menuTreeContext';

const iconBtn = 'p-0.5 hover:bg-stone-200 rounded text-stone-400 hover:text-stone-600 cursor-pointer';

/* ── Inline text input (title / link edition) ── */
export function InlineInput({ defaultValue, placeholder, onConfirm, onCancel, autoFocus = true }) {
  const ref = useRef(null);
  const cancelledRef = useRef(false);
  const [value, setValue] = useState(defaultValue || '');

  useEffect(() => {
    if (autoFocus && ref.current) ref.current.focus();
  }, [autoFocus]);

  const submit = () => {
    if (cancelledRef.current) return;
    const v = value.trim();
    if (v) onConfirm(v);
    else onCancel();
  };

  return (
    <input
      ref={ref}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') submit();
        if (e.key === 'Escape') { cancelledRef.current = true; onCancel(); }
      }}
      onBlur={submit}
      placeholder={placeholder}
      className="w-full text-xs px-2 py-1 bg-white border border-blue-400 rounded-md outline-none ring-2 ring-blue-200/50 text-stone-800 placeholder:text-stone-300"
    />
  );
}

/* ── Single tree row ── */
export function MenuItemRow({
  item, depth, collapsed, hasChildren, onCollapse,
  editMode, onEditConfirm, onEditCancel,
  onStartRename, onStartLinkEdit, onSettings, onCreate, onDelete, onToggleSubtree,
  dragHandleProps, isDragging,
}) {
  const pad = { paddingLeft: `${depth * 20 + 10}px` };

  if (editMode) {
    const isLink = editMode === 'link';
    return (
      <div className="py-0.5" style={{ ...pad, paddingRight: 8 }}>
        <InlineInput
          defaultValue={isLink ? displayUri(item.uri) : item.title}
          placeholder={isLink ? '/chemin, https://…, <front>' : 'Titre…'}
          onConfirm={(v) => onEditConfirm(item, v)}
          onCancel={onEditCancel}
        />
      </div>
    );
  }

  return (
    <div
      className={`flex items-center gap-1.5 py-1.5 px-2 rounded-lg text-xs transition-all duration-150 group w-full relative select-none ${
        isDragging ? 'opacity-40 bg-blue-50 ring-1 ring-blue-300' : 'text-stone-700 hover:bg-stone-100'
      }`}
      style={pad}
    >
      <span {...dragHandleProps} className="flex-shrink-0 w-3 text-stone-300 hover:text-stone-500 cursor-grab active:cursor-grabbing touch-none opacity-0 group-hover:opacity-100 transition-opacity" aria-label="Réorganiser">
        <GripVertical size={12} />
      </span>

      <span
        className="flex-shrink-0 w-4 text-stone-400 hover:text-stone-600 cursor-pointer"
        onClick={(e) => { e.stopPropagation(); if (hasChildren) onCollapse?.(); }}
      >
        {hasChildren ? (collapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />) : <span className="inline-block w-3.5" />}
      </span>

      <span className="flex-shrink-0 w-4 text-stone-400"><LinkIcon size={13} /></span>

      {/* Title + link */}
      <span className="flex-1 min-w-0 flex items-center gap-1.5">
        <span className="truncate max-w-[45%]">
          {item.title}{!item.enabled && <span className="ml-1 text-[10px] text-amber-500">(désactivé)</span>}
        </span>
        <span onClick={(e) => { e.stopPropagation(); onStartRename?.(item); }} className={`${iconBtn} opacity-0 group-hover:opacity-100 transition-opacity`} aria-label="Modifier le titre">
          <Pencil size={11} />
        </span>
        <span className="text-stone-300">·</span>
        <span className="truncate text-stone-400 italic min-w-0">{displayUri(item.uri) || '—'}</span>
        <span onClick={(e) => { e.stopPropagation(); onStartLinkEdit?.(item); }} className={`${iconBtn} opacity-0 group-hover:opacity-100 transition-opacity`} aria-label="Modifier le lien">
          <Pencil size={11} />
        </span>
      </span>

      {/* Actions */}
      <span className="opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 flex items-center gap-0.5">
        {hasChildren && (
          <span onClick={(e) => { e.stopPropagation(); onToggleSubtree?.(item, !collapsed); }} className={iconBtn} aria-label={collapsed ? 'Déplier le sous-arbre' : 'Replier le sous-arbre'}>
            {collapsed ? <ChevronsUpDown size={12} /> : <ChevronsDownUp size={12} />}
          </span>
        )}
        <span onClick={(e) => { e.stopPropagation(); onCreate?.('before', item); }} className={iconBtn} aria-label="Ajouter avant" title="Ajouter avant">
          <ArrowUpToLine size={12} />
        </span>
        <span onClick={(e) => { e.stopPropagation(); onCreate?.('after', item); }} className={iconBtn} aria-label="Ajouter après" title="Ajouter après">
          <ArrowDownToLine size={12} />
        </span>
        <span onClick={(e) => { e.stopPropagation(); onCreate?.('child', item); }} className={iconBtn} aria-label="Ajouter un enfant" title="Ajouter un enfant">
          <CornerDownRight size={12} />
        </span>
        <span onClick={(e) => { e.stopPropagation(); onSettings?.(item); }} className={iconBtn} aria-label="Paramètres">
          <Settings size={12} />
        </span>
        <span onClick={(e) => { e.stopPropagation(); onDelete?.(item); }} className="p-0.5 hover:bg-red-100 rounded text-stone-400 hover:text-red-600 cursor-pointer" aria-label="Supprimer">
          <Trash2 size={12} />
        </span>
      </span>
    </div>
  );
}

export const MenuDndItemComponent = forwardRef(function MenuDndItem(props, ref) {
  const ctx = useContext(MenuTreeCtx);
  const { item, depth, handleProps, ghost, clone, wrapperRef, style, collapsed, onCollapse } = props;
  const srcItem = item._item;
  if (!srcItem) return null;

  if (clone) {
    return (
      <div ref={ref} className="menu-tree-drag-overlay">
        <div className="flex items-center gap-1.5 py-1.5 px-3 rounded-lg text-xs bg-white shadow-lg ring-1 ring-blue-200 text-stone-700 font-medium max-w-[220px]">
          <LinkIcon size={13} className="text-blue-500 flex-shrink-0" />
          <span className="truncate">{srcItem.title}</span>
        </div>
      </div>
    );
  }

  return (
    <li ref={wrapperRef} style={{ ...style, listStyle: 'none' }}>
      <div ref={ref}>
        <MenuItemRow
          item={srcItem}
          depth={depth}
          collapsed={collapsed}
          hasChildren={(item.children?.length ?? 0) > 0}
          onCollapse={onCollapse}
          editMode={ctx.editing?.key === srcItem.key ? ctx.editing.mode : null}
          onEditConfirm={ctx.onEditConfirm}
          onEditCancel={ctx.onEditCancel}
          onStartRename={ctx.onStartRename}
          onStartLinkEdit={ctx.onStartLinkEdit}
          onSettings={ctx.onSettings}
          onCreate={ctx.onCreate}
          onDelete={ctx.onDelete}
          onToggleSubtree={ctx.onToggleSubtree}
          dragHandleProps={handleProps}
          isDragging={ghost}
        />
      </div>
    </li>
  );
});
